<template>
  <div>
    <div :class="containerClass">
      <div class="d-flex justify-content-center" v-if="getLoggedInUser.role === USER_ROLES.STUDENT">
        <div class="card card-block card-stretch card-height col-md-12">
          <div class="card-body text-center">
            <p class="text-90">
              Welcome to ai skill card practice! Click below to create an ai skill card performing a skill that you've
              learned in the program.
            </p>
            <b-btn @click.prevent="openSkillMapForm()" variant="primary"
              ><i class="material-icons mr-1">videocam</i>Create Skill Card</b-btn
            >
          </div>
        </div>
      </div>

      <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-24pt" style="white-space: nowrap">
        <h2 class="d-sm-block" :class="isMobileScreen ? 'mb-3' : 'mb-0'">
          Skill Cards
          <i
            class="material-icons icon-16pt clickable-item"
            v-b-popover.hover.top="'Explore the skill cards of your students.'"
            >info</i
          >
        </h2>
        <div class="w-auto ml-sm-auto mb-sm-0 justify-content-end" style="display: flex;" v-if="!isMobileScreen">
          <div class="custom-select-icon mr-2">
            <b-form-select
              class="custom-select-icon__select"
              style="min-width: 185px"
              v-model="filters.status"
              :options="SkillStatus"
              text-field="text"
              value-field="value"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ 'Select status' }}
                </b-form-select-option>
              </template>
            </b-form-select>
            <span class="material-icons custom-select-icon__icon">
              <template>
                sort
              </template>
            </span>
          </div>
          <div class="custom-select-icon">
            <b-form-select
              class="custom-select-icon__select"
              style="min-width: 185px"
              v-model="filters.skillmap"
              :options="schoolSkillMaps"
              text-field="label"
              value-field="id"
              :disabled="isLoadingSkillmaps"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ isLoadingSkillmaps ? 'Loading skills...' : 'Select a skill' }}
                </b-form-select-option>
              </template>
            </b-form-select>

            <span class="material-icons custom-select-icon__icon">
              <template v-if="isLoadingSkillmaps">
                <i
                  class="spinner-border spinner-border-sm text-secondary"
                  style="width: 1rem; height: 1rem;"
                  role="status"
                ></i>
              </template>
              <template v-else>
                sort
              </template>
            </span>
          </div>
        </div>
        <div v-if="isMobileScreen">
          <div class="custom-select-icon">
            <b-form-select
              class="custom-select-icon__select"
              style="min-width: 185px"
              v-model="filters.status"
              :options="SkillStatus"
              text-field="text"
              value-field="value"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ 'Select status' }}
                </b-form-select-option>
              </template>
            </b-form-select>
            <span class="material-icons custom-select-icon__icon">
              <template>
                sort
              </template>
            </span>
          </div>
          <div class="custom-select-icon mt-2">
            <b-form-select
              class="custom-select-icon__select"
              style="min-width: 185px"
              v-model="filters.skillmap"
              :options="schoolSkillMaps"
              text-field="label"
              value-field="id"
              :disabled="isLoadingSkillmaps"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ isLoadingSkillmaps ? 'Loading skills...' : 'Select a skill' }}
                </b-form-select-option>
              </template>
            </b-form-select>

            <span class="material-icons custom-select-icon__icon">
              <template v-if="isLoadingSkillmaps">
                <i
                  class="spinner-border spinner-border-sm text-secondary"
                  style="width: 1rem; height: 1rem;"
                  role="status"
                ></i>
              </template>
              <template v-else>
                sort
              </template>
            </span>
          </div>
        </div>
      </div>

      <page-separator
        :title="
          !isLoading
            ? `showing ${totalSkillCards ? `${pageFromCount}-${pageToCount}` : 0} of ${totalSkillCards} Skill Cards`
            : ''
        "
      />

      <div class="page-section d-flex justify-content-center" v-if="!skillCard.length && !isLoading">
        <div class="card card-block card-stretch card-height col-md-8">
          <div class="card-body text-center">
            <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
            <h4>No Skill Cards Found</h4>

            <p class="text-muted">
              Couldn't find any skill cards.
            </p>
          </div>
        </div>
      </div>

      <b-skeleton-wrapper :loading="isLoading" v-else>
        <template #loading>
          <div class="row">
            <div class="col-lg-4 col-md-4 col-sm-6" v-for="item in [1, 2, 3]" :key="item">
              <div class="card card-sm card--elevated p-relative">
                <span class="js-image">
                  <b-skeleton-img width="100%" class="pb-0 mb-0"></b-skeleton-img>
                </span>

                <div class="card-body">
                  <div class="d-flex">
                    <div class="avatar avatar-sm rounded-circle o-hidden" style="border: 1px solid #dfe2e6;">
                      <b-skeleton-img class="avatar-img" width="160" height="100%"></b-skeleton-img>
                    </div>
                    <span class="ml-2 w-100">
                      <div class="card-title"><b-skeleton width="100%" /></div>
                      <div class="text-muted"><b-skeleton width="50%"></b-skeleton></div>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div class="row">
          <div
            v-for="(card, index) in skillCard"
            :key="card.id"
            :id="`skill-${card.id}`"
            class="col-lg-4 col-md-4 col-sm-6 clickable-item"
            :style="{ marginBottom: isMobileScreen ? '25px' : '10px' }"
          >
            <div style="height: 310px; margin-bottom: 10px;">
              <div
                class="card"
                @click="openModal(card)"
                @mouseenter="handleMouse(index)"
                @mouseleave="handleMouse(index)"
              >
                <div class="card-image">
                  <img
                    :src="card.image ? card.image : require('@/assets/images/placeholders/default_image.jpeg')"
                    :alt="title"
                  />
                  <b-badge
                    class="precision-badge"
                    :class="'border border-' + getStatusColor(card.status)"
                    :style="{ fontSize: '12px' }"
                    variant="none"
                  >
                    {{ STATUSES[card.status] }}
                  </b-badge>

                  <div class="hover-overlay" :class="{ active: showHover[index] }">
                    <div class="hover-content">
                      <i class="fas fa-eye clickable-item ml-2 eye-icon"></i>
                      <span>View Details</span>
                    </div>
                  </div>
                </div>

                <div class="card-content p-2 d-flex justify-content-between align-items-center">
                  <div class="d-flex ">
                    <user-avatar
                      slot="aside"
                      :user="card.student.user"
                      circle="md"
                      size="md"
                      :file-url="card.student.user.avatar_url"
                      variant="dark"
                    >
                    </user-avatar>
                    <div class="d-flex flex-column ml-2">
                      <div class="card-title">{{ card.student.user.first_name }} {{ card.student.user.last_name }}</div>
                      <div class="text-muted d-flex align-items-center mt-1">
                        <div
                          class="material-icons icon-16pt text-black-50 mr-1"
                          style="font-size: 12px; vertical-align: middle;"
                        >
                          access_time
                        </div>
                        <div style="line-height: 1">{{ formatTime(card.created_at) }} ago</div>
                      </div>
                    </div>
                  </div>
                  <div style="align-self: flex-end;  color:deepskyblue; margin-bottom:-4px">
                    <div class="" v-b-popover.hover.top="'Precision'" v-if="card.precision !== null">
                      <i class="fas fa-bullseye clickable-item eye-icon mr-1" style="font-size: medium;"></i
                      >{{ card.precision }}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-skeleton-wrapper>
      <skill-card-result-modal
        :is-open="showSkillCardModal"
        @hide-modal="showSkillCardModal = false"
        @update-status="updateStatus"
        :card="currentCard"
      />
      <pagination
        v-if="totalSkillCards > skillCard.length"
        v-model="currentPage"
        :total-rows="totalSkillCards"
        :per-page="perPage"
        @change="onPageChange"
        class="mb-3"
      />
    </div>
  </div>
</template>
<script>
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import UserAvatar from '@/components/User/UserAvatar.vue';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { formatTime } from '../../../common/utils';
import { USER_ROLES, APPROVAL_STATUS, SKILLCARD_STATUS } from '../../../common/constants';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import SkillCardResultModal from '@/components/SkillCard/SkillCardResultModal.vue';

export default {
  components: {
    Pagination,
    UserAvatar,
    PageSeparator,
    SkillCardResultModal,
  },
  extends: Page,

  data() {
    return {
      title: 'Skill Maps',
      schoolSkillMaps: [],
      filters: {
        skillmap: null,
        status: null,
      },
      USER_ROLES,
      APPROVAL_STATUS,
      totalSkillCards: 0,
      skillCard: [],
      isLoading: false,
      perPage: 16,
      showHover: [],
      windowWidth: window.innerWidth,
      likeCountLoading: false,
      likesCount: 0,
      isLikingSkillCard: false,
      isUnlikingSkillCard: false,
      flippedCards: {},
      isLoadingSkillmaps: false,
      STATUSES: { pending: 'Pending', approved: 'Approved', rejected: 'Not Approved' },
      showSkillCardModal: false,
      currentCard: {},
      SkillStatus: [
        { value: 'pending', text: 'Pending' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Not Approved' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills', active: true },
      ];
    },

    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetchAiSkillmapCards();
      },
    },
  },

  methods: {
    ...mapActions('aiSkills', ['getAllSkillMapCards', 'getAllSkillMaps', 'updateSkillCardStatus']),

    formatTime,
    get,
    handleMouse(index) {
      this.$set(this.showHover, index, !this.showHover[index]);
    },
    getStatusColor(status) {
      if (status === SKILLCARD_STATUS.APPROVED) {
        return 'success badge-success';
      } else if (status === SKILLCARD_STATUS.REJECTED) {
        return 'danger badge-danger';
      } else {
        return 'warning badge-warning';
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    async fetchSchoolSkillmaps() {
      this.isLoadingSkillmaps = true;
      const resp = await this.getAllSkillMaps();
      this.schoolSkillMaps = resp.data
        ? resp.data.map(skillmap => {
            return { id: skillmap.id, label: skillmap.title };
          })
        : [];
      this.isLoadingSkillmaps = false;
    },
    openSkillMapForm() {
      this.$router.push({ name: 'student-create-skillmap' });
    },

    openModal(card) {
      this.currentCard = {
        step: card.step ? card.step.step : '',
        skillmap: card.skill_map.title,
        results: card.results,
        precision: card.precision,
        status: card.status,
        id: card.id,
      };
      this.showSkillCardModal = true;
    },
    hideModal() {
      this.showSkillCardModal = false;
    },

    async fetchAiSkillmapCards(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();
      try {
        const response = await this.getAllSkillMapCards({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...params,
          ...(this.filters.skillmap ? { skill_map__id: this.filters.skillmap } : {}),
          ...(this.filters.status ? { status: this.filters.status } : {}),
        });
        this.skillCard = response.data.results;

        this.currentPage = pageNum;
        this.totalSkillCards = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async updateStatus({ id, status }) {
      try {
        await this.updateSkillCardStatus({ id: id, status: status });
        this.showSkillCardModal = false;
        this.makeToast({ variant: 'success', msg: 'Status updated successfully!' });
        this.fetchAiSkillmapCards();
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: 'Status update failed!' });
      }
    },

    onPageChange(pageNum) {
      this.fetchAiSkillmapCards(pageNum);
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);
    await this.fetchAiSkillmapCards();
    this.fetchSchoolSkillmaps();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.card-image {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.card:hover .card-image img {
  transform: scale(1.05);
}

.hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.hover-overlay.active {
  opacity: 1;
}

.hover-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  text-align: center;
}

.eye-icon {
  font-size: 32px;
  margin-bottom: 5px;
}

.hover-content span {
  font-size: 16px;
  font-weight: 600;
}

.eye-icon {
  font-size: larger;
}

.scrollable-content {
  background-color: #f1f1f1;
  border-radius: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  padding: 15px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 15px;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  /* This is key to prevent scroll bubbling */
  overscroll-behavior: contain;
  scroll-behavior: smooth;
}

.precision-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  font-weight: bold;
}

@media (max-width: 767.98px) {
  .card-body {
    padding: 0.75rem;
  }

  .card-title {
    font-size: 0.9rem;
  }

  .text-muted {
    font-size: 0.8rem;
  }
}
</style>
